import React, { useState } from "react";
import useSWR, { mutate } from "swr";
import { api, STATUS_TO_PILL } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const fetcher = (u) => api.get(u).then(r=>r.data);

export function ProductionOrdersPage() {
  const { data: pos } = useSWR("/production-orders", fetcher);
  const { data: products } = useSWR("/products", fetcher);
  const { data: machines } = useSWR("/machines", fetcher);
  const { data: moulds } = useSWR("/moulds", fetcher);
  const { data: customers } = useSWR("/customers", fetcher);
  const { data: boms } = useSWR("/boms", fetcher);
  const [show, setShow] = useState(false);
  const [f, setF] = useState({});

  const pMap = Object.fromEntries((products||[]).map(p=>[p.product_id,p]));
  const cMap = Object.fromEntries((customers||[]).map(c=>[c.customer_id,c]));

  const submit = async () => {
    try {
      await api.post("/production-orders", f);
      toast.success("Production order created");
      setShow(false); setF({}); mutate("/production-orders");
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const setStatus = async (po_id, status) => {
    try { await api.patch(`/production-orders/${po_id}/status`, { status }); toast.success("Status updated"); mutate("/production-orders"); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const nextStatus = (s) => ({ PLANNED:"RELEASED", RELEASED:"RUNNING", RUNNING:"COMPLETED", PAUSED:"RUNNING", COMPLETED:"QC_HOLD", QC_HOLD:"QC_RELEASED", QC_RELEASED:"CLOSED" }[s]);

  return (
    <div className="space-y-4" data-testid="production-orders-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Production Orders</h1>
          <p className="text-sm text-slate-500">{(pos||[]).length} orders</p>
        </div>
        <button data-testid="btn-create-production-order" onClick={()=>setShow(true)} className="bg-sky-600 hover:bg-sky-700 text-white text-sm px-3 py-1.5 rounded-md">+ New Order</button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-auto">
        <table className="data-table" data-testid="production-orders-table">
          <thead><tr><th>PO No</th><th>Product</th><th>Customer</th><th>Machine</th><th>Planned</th><th>Actual</th><th>Rejected</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>
            {(pos||[]).map(p => (
              <tr key={p.po_id} data-testid={`po-row-${p.po_no}`}>
                <td className="font-mono text-xs">{p.po_no}</td>
                <td>{pMap[p.product_id]?.name || p.product_id}</td>
                <td>{cMap[p.customer_id]?.name || "-"}</td>
                <td className="text-xs font-mono">{p.machine_id}</td>
                <td className="font-mono">{p.planned_qty?.toLocaleString()}</td>
                <td className="font-mono">{p.actual_qty?.toLocaleString()}</td>
                <td className="font-mono">{p.rejected_qty?.toLocaleString()}</td>
                <td><span className={`pill ${STATUS_TO_PILL(p.status)}`}>{p.status}</span></td>
                <td>
                  {nextStatus(p.status) && (
                    <button data-testid={`btn-advance-${p.po_no}`} onClick={()=>setStatus(p.po_id, nextStatus(p.status))} className="text-xs px-2 py-1 rounded bg-sky-600 text-white">→ {nextStatus(p.status)}</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {show && (
        <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl p-5" data-testid="po-modal">
            <h3 className="font-semibold text-slate-900 mb-4">Create Production Order</h3>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Product *"><select data-testid="po-input-product" value={f.product_id||""} onChange={e=>setF({...f,product_id:e.target.value})} className="input"><option value="">Select</option>{(products||[]).map(p=><option key={p.product_id} value={p.product_id}>{p.name}</option>)}</select></Field>
              <Field label="Customer"><select data-testid="po-input-customer" value={f.customer_id||""} onChange={e=>setF({...f,customer_id:e.target.value})} className="input"><option value="">Select</option>{(customers||[]).map(c=><option key={c.customer_id} value={c.customer_id}>{c.name}</option>)}</select></Field>
              <Field label="Machine"><select data-testid="po-input-machine" value={f.machine_id||""} onChange={e=>setF({...f,machine_id:e.target.value})} className="input"><option value="">Select</option>{(machines||[]).map(m=><option key={m.machine_id} value={m.machine_id}>{m.code} {m.name}</option>)}</select></Field>
              <Field label="Mould"><select data-testid="po-input-mould" value={f.mould_id||""} onChange={e=>setF({...f,mould_id:e.target.value})} className="input"><option value="">Select</option>{(moulds||[]).map(m=><option key={m.mould_id} value={m.mould_id}>{m.code}</option>)}</select></Field>
              <Field label="BOM"><select data-testid="po-input-bom" value={f.bom_id||""} onChange={e=>setF({...f,bom_id:e.target.value})} className="input"><option value="">Select</option>{(boms||[]).map(b=><option key={b.bom_id} value={b.bom_id}>{b.bom_id} rev{b.revision}</option>)}</select></Field>
              <Field label="Planned Qty *"><input data-testid="po-input-planned" type="number" value={f.planned_qty||""} onChange={e=>setF({...f,planned_qty:Number(e.target.value)})} className="input"/></Field>
              <Field label="Cavity"><input type="number" value={f.cavity||""} onChange={e=>setF({...f,cavity:Number(e.target.value)})} className="input"/></Field>
              <Field label="Cycle (s)"><input type="number" value={f.cycle_time||""} onChange={e=>setF({...f,cycle_time:Number(e.target.value)})} className="input"/></Field>
              <Field label="Shift"><select value={f.shift||"A"} onChange={e=>setF({...f,shift:e.target.value})} className="input"><option>A</option><option>B</option><option>C</option></select></Field>
              <Field label="Due Date"><input type="date" value={f.due_date||""} onChange={e=>setF({...f,due_date:e.target.value})} className="input"/></Field>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={()=>setShow(false)} className="text-sm px-3 py-1.5 rounded-md border border-slate-200">Cancel</button>
              <button data-testid="btn-submit-po" onClick={submit} className="text-sm px-3 py-1.5 rounded-md bg-sky-600 text-white">Create</button>
            </div>
          </div>
        </div>
      )}
      <style>{`.input{width:100%;font-size:.85rem;border:1px solid #E2E8F0;border-radius:6px;padding:.4rem .5rem;background:#fff}`}</style>
    </div>
  );
}

const Field = ({label, children}) => (<div><label className="text-[11px] uppercase font-semibold text-slate-500">{label}</label>{children}</div>);

export function ProductionEntryPage() {
  const { data: allPos } = useSWR("/production-orders", fetcher);
  const { data: entries } = useSWR("/production-entries", fetcher);
  const { data: products } = useSWR("/products", fetcher);
  const pMap2 = Object.fromEntries((products||[]).map(p=>[p.product_id,p]));
  const pos = (allPos||[]).filter(p=>["RUNNING","RELEASED","PAUSED"].includes(p.status));
  const poMap = Object.fromEntries((allPos||[]).map(p=>[p.po_id, p]));
  const [po_id, setPo] = useState("");
  const [good, setGood] = useState(0);
  const [rej, setRej] = useState(0);
  const [scrap, setScrap] = useState(0);
  const [rework, setRework] = useState(0);

  const submit = async () => {
    if (!po_id) return toast.error("Select order");
    try {
      await api.post("/production-entries", { po_id, good_qty: good, rejected_qty: rej, scrap_qty: scrap, rework_qty: rework });
      toast.success(`Entry saved: ${good+rej+rework} pcs`);
      setGood(0); setRej(0); setScrap(0); setRework(0);
      mutate("/production-entries"); mutate("/production-orders");
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const inc = (setter) => (n) => setter(v => v + n);

  return (
    <div className="space-y-4" data-testid="production-entry-page">
      <h1 className="text-2xl font-bold text-slate-900">Production Entry</h1>
      <p className="text-sm text-slate-500">Operator-friendly touch entry for shop floor tablets.</p>

      <div className="bg-white border border-slate-200 rounded-xl p-4 max-w-2xl">
        <label className="text-[11px] uppercase font-semibold text-slate-500">Select Running Order</label>
        <select data-testid="entry-select-po" value={po_id} onChange={e=>setPo(e.target.value)} className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 mt-1">
          <option value="">-- Select --</option>
          {pos.map(p=><option key={p.po_id} value={p.po_id}>{p.po_no} — {pMap2[p.product_id]?.name || p.product_id}</option>)}
        </select>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
          {[["Good", good, setGood, "good"],["Rejected", rej, setRej, "rej"],["Rework", rework, setRework, "rework"],["Scrap", scrap, setScrap, "scrap"]].map(([lbl,val,setter,tid])=>(
            <div key={tid} className="bg-slate-50 rounded-lg p-3 text-center">
              <div className="text-[11px] uppercase text-slate-500">{lbl}</div>
              <div data-testid={`entry-${tid}-value`} className="font-mono text-2xl font-extrabold my-1">{val}</div>
              <div className="flex flex-wrap justify-center gap-1">
                {[100,500,1000].map(n=><button key={n} data-testid={`btn-${tid}-add-${n}`} onClick={()=>inc(setter)(n)} className="text-[11px] px-2 py-1 rounded bg-white border border-slate-200">+{n}</button>)}
                <button data-testid={`btn-${tid}-reset`} onClick={()=>setter(0)} className="text-[11px] px-2 py-1 rounded bg-white border border-slate-200 text-red-600">Reset</button>
              </div>
            </div>
          ))}
        </div>
        <button data-testid="btn-submit-entry" onClick={submit} className="mt-5 w-full bg-sky-600 hover:bg-sky-700 text-white py-3 rounded-lg font-semibold">Submit Entry ({good+rej+rework} pcs)</button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl">
        <div className="px-4 py-2 font-semibold text-slate-800 border-b border-slate-100">Recent Entries</div>
        <div className="overflow-auto max-h-96">
          <table className="data-table" data-testid="entries-table">
            <thead><tr><th>Time</th><th>PO</th><th>Good</th><th>Rejected</th><th>Rework</th><th>Total</th><th>Rej %</th></tr></thead>
            <tbody>
              {(entries||[]).slice(0,50).map(e=>(
                <tr key={e.entry_id}>
                  <td className="text-xs font-mono">{e.timestamp?.slice(0,16).replace("T"," ")}</td>
                  <td className="text-xs font-mono">{poMap[e.po_id]?.po_no || e.po_id}</td>
                  <td className="font-mono">{e.good_qty}</td>
                  <td className="font-mono">{e.rejected_qty}</td>
                  <td className="font-mono">{e.rework_qty}</td>
                  <td className="font-mono">{e.total}</td>
                  <td className="font-mono">{e.rejection_pct}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export function DowntimePage() {
  const { data: dts } = useSWR("/downtime", fetcher);
  const { data: machines } = useSWR("/machines", fetcher);
  const [f, setF] = useState({ category: "MACHINE_BREAKDOWN", duration_min: 0 });
  const cats = ["MACHINE_BREAKDOWN","MOULD_PROBLEM","MATERIAL_SHORTAGE","POWER_FAILURE","QUALITY_PROBLEM","OPERATOR_SHORTAGE","CHANGEOVER","CLEANING","MAINTENANCE","NO_ORDER","OTHER"];
  const submit = async () => {
    if (!f.machine_id || !f.reason) return toast.error("Machine & reason required");
    try { await api.post("/downtime", f); toast.success("Downtime logged"); setF({category:"MACHINE_BREAKDOWN",duration_min:0}); mutate("/downtime"); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  return (
    <div className="space-y-4" data-testid="downtime-page">
      <h1 className="text-2xl font-bold text-slate-900">Downtime Tracker</h1>
      <div className="bg-white border border-slate-200 rounded-xl p-4 grid grid-cols-1 md:grid-cols-5 gap-3">
        <div><label className="text-[11px] uppercase font-semibold text-slate-500">Machine *</label><select data-testid="dt-machine" value={f.machine_id||""} onChange={e=>setF({...f,machine_id:e.target.value})} className="w-full text-sm border border-slate-200 rounded px-2 py-1.5"><option value="">Select</option>{(machines||[]).map(m=><option key={m.machine_id} value={m.machine_id}>{m.code}</option>)}</select></div>
        <div><label className="text-[11px] uppercase font-semibold text-slate-500">Category</label><select data-testid="dt-category" value={f.category} onChange={e=>setF({...f,category:e.target.value})} className="w-full text-sm border border-slate-200 rounded px-2 py-1.5">{cats.map(c=><option key={c}>{c}</option>)}</select></div>
        <div><label className="text-[11px] uppercase font-semibold text-slate-500">Reason *</label><input data-testid="dt-reason" value={f.reason||""} onChange={e=>setF({...f,reason:e.target.value})} className="w-full text-sm border border-slate-200 rounded px-2 py-1.5"/></div>
        <div><label className="text-[11px] uppercase font-semibold text-slate-500">Duration (min)</label><input data-testid="dt-duration" type="number" value={f.duration_min} onChange={e=>setF({...f,duration_min:Number(e.target.value)})} className="w-full text-sm border border-slate-200 rounded px-2 py-1.5"/></div>
        <div className="flex items-end"><button data-testid="btn-log-downtime" onClick={submit} className="w-full bg-sky-600 text-white text-sm py-1.5 rounded">Log Downtime</button></div>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl overflow-auto">
        <table className="data-table" data-testid="downtime-table">
          <thead><tr><th>Time</th><th>Machine</th><th>Category</th><th>Reason</th><th>Duration</th><th>Shift</th></tr></thead>
          <tbody>{(dts||[]).map(d=>(<tr key={d.downtime_id}><td className="text-xs font-mono">{d.start_time?.slice(0,16).replace("T"," ")}</td><td className="text-xs font-mono">{d.machine_id}</td><td><span className="pill pill-idle">{d.category}</span></td><td>{d.reason}</td><td className="font-mono">{d.duration_min} min</td><td>{d.shift}</td></tr>))}</tbody>
        </table>
      </div>
    </div>
  );
}

export function QCPage() {
  const { data: qcs } = useSWR("/qc-batches", fetcher);
  const { data: products } = useSWR("/products", fetcher);
  const pMap = Object.fromEntries((products||[]).map(p=>[p.product_id,p]));
  const decide = async (qc, status) => {
    try { await api.patch(`/qc-batches/${qc.qc_id}`, { status, warehouse_id: "wh_fg" }); toast.success(`Batch ${status}`); mutate("/qc-batches"); mutate("/fg-inventory"); }
    catch(e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  return (
    <div className="space-y-4" data-testid="qc-page">
      <h1 className="text-2xl font-bold text-slate-900">Quality Control</h1>
      <div className="bg-white border border-slate-200 rounded-xl overflow-auto">
        <table className="data-table" data-testid="qc-table">
          <thead><tr><th>Batch</th><th>Product</th><th>PO</th><th>Qty</th><th>Type</th><th>Status</th><th>Decision</th></tr></thead>
          <tbody>
            {(qcs||[]).map(q=>(
              <tr key={q.qc_id}>
                <td className="font-mono text-xs">{q.batch}</td>
                <td>{pMap[q.product_id]?.name || q.product_id}</td>
                <td className="text-xs font-mono">{q.po_id}</td>
                <td className="font-mono">{q.qty}</td>
                <td>{q.inspection_type}</td>
                <td><span className={`pill ${STATUS_TO_PILL(q.status)}`}>{q.status}</span></td>
                <td className="space-x-1">
                  {q.status==="PENDING" && <>
                    <button data-testid={`btn-approve-${q.qc_id}`} onClick={()=>decide(q,"APPROVED")} className="text-xs px-2 py-1 rounded bg-emerald-600 text-white">Approve</button>
                    <button data-testid={`btn-reject-${q.qc_id}`} onClick={()=>decide(q,"REJECTED")} className="text-xs px-2 py-1 rounded bg-red-600 text-white">Reject</button>
                    <button data-testid={`btn-hold-${q.qc_id}`} onClick={()=>decide(q,"HOLD")} className="text-xs px-2 py-1 rounded bg-purple-600 text-white">Hold</button>
                  </>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
